<template>
  <div id="app">
    <!-- Version Update Modal - Blocks user until they update -->
    <div v-if="shouldShowVersionModal" class="version-modal-overlay">
      <div class="version-modal">
        <div class="version-modal__icon">🔄</div>
        <h2 class="version-modal__title">Update Available</h2>
        <p class="version-modal__text">
          A new version of Petty Cash is available. Please update to continue.
        </p>
        <p class="version-modal__version">
          Current: {{ storedVersion }} → Latest: {{ latestVersion }}
        </p>
        <button class="version-modal__btn" @click="updateVersion">
          {{ updateCountdown > 0 ? `Update Now (${updateCountdown})` : 'Updating...' }}
        </button>
      </div>
    </div>

    <Loader />
    <router-view @login-success="setupInactivityTimer"/>
  </div>
</template>

<script>
import Loader from "@/components/layout/Loader";
import Session from '@/utils/session';
import Swal from "sweetalert2";
export default {
  data() {
    return {
      sessionTimeout: 15,
      // Version Update - GINGER-20260512: Auto-update feature
      showVersionUpdate: false,
      latestVersion: '',
      storedVersion: '',
      updateCountdown: 5,
      updateTimer: null,
    }
  },
  computed: {
    // Show modal on version mismatch - blocks app until update
    shouldShowVersionModal() {
      return this.showVersionUpdate && this.latestVersion && this.storedVersion && this.storedVersion !== this.latestVersion;
    },
  },
  components: {Loader},
  computed : {
    loading(){
      return this.$store.getters.getLoadingState;
    },
  },
  created() {
    // GINGER-20260512: Version check triggers existing PWA update system
    this.checkVersionUpdate();

    // GINGER-20260710-FILE4-LIVE: Hydrate current_user + sessionId from storage BEFORE
    // anything else runs so NavigationDrawer / Dashboard / etc. have user.id available
    // instantly on page refresh. Belt-and-braces alongside v1.4.3 envelope refactor.
    this.$store.dispatch('user/restoreSession');

    const storedValue = localStorage.getItem('sessionValue');
    if (storedValue === null) {
      localStorage.setItem('sessionValue', this.sessionTimeout)
      localStorage.setItem('sessionTimeout', this.sessionTimeout);
    }
    this.checkSession(); // Check if the user is logged in when the app loads
    this.setupInactivityTimer(); // Set up the session timeout timer

    const enabled = this.getIsSessionTimeoutEnabled();
    this.setIsSessionTimeoutEnabled(enabled);
  },
  methods : {
    // GINGER-20260512: Version check - shows blocking modal on version mismatch
    async checkVersionUpdate() {
      try {
        console.log('🔍 Version check starting...');
        const response = await fetch('/version.json?_=' + Date.now());
        const versionData = await response.json();
        this.latestVersion = versionData.version;
        this.storedVersion = localStorage.getItem('appVersion') || '';

        console.log('📦 Latest version:', this.latestVersion);
        console.log('💾 Stored version:', this.storedVersion);

        if (this.storedVersion !== this.latestVersion) {
          // If storedVersion is empty (first time), just save and don't show modal
          if (!this.storedVersion) {
            localStorage.setItem('appVersion', this.latestVersion);
            console.log('✅ First time - saved version');
          } else {
            console.log('🔄 Version mismatch - showing update modal');
            this.showVersionUpdate = true;
            this.startCountdown();
          }
        } else {
          console.log('✅ Version matches - no update needed');
        }
      } catch (e) {
        console.warn('Version check failed:', e);
      }
    },
    updateVersion() {
      // Use Service Worker to update the app
      this.clearCountdown();
      // Save the version first so reload doesn't show modal again
      localStorage.setItem('appVersion', this.latestVersion);
      try {
        if (navigator && navigator.serviceWorker && navigator.serviceWorker.getRegistration) {
          navigator.serviceWorker.getRegistration().then((registration) => {
            if (registration && registration.waiting) {
              // Send skip waiting message to SW
              registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        }
      } catch (e) {
        console.warn('Service Worker update failed:', e);
      }
      // Also try to reload the page as fallback
      setTimeout(() => {
        window.location.reload();
      }, 500);
    },
    startCountdown() {
      this.updateCountdown = 5;
      this.updateTimer = setInterval(() => {
        this.updateCountdown--;
        if (this.updateCountdown <= 0) {
          this.clearCountdown();
          // Auto-update using Service Worker
          this.updateVersion();
        }
      }, 1000);
    },
    clearCountdown() {
      if (this.updateTimer) {
        clearInterval(this.updateTimer);
        this.updateTimer = null;
      }
    },

    getIsSessionTimeoutEnabled() {
      const key = 'isSessionTimeoutEnabled';
      const storedValue = localStorage.getItem(key);
      return storedValue !== null ? storedValue === 'true' : true;
    },
    setIsSessionTimeoutEnabled(value) {
      localStorage.setItem('isSessionTimeoutEnabled', value.toString());
    },
    checkSession() {
      // Check if session_id exists in localStorage
      const sessionId = localStorage.getItem('session_id');
      // if (!sessionId) {
      //   this.$router.push({ name: "login" }); // If no session ID, redirect to login
      // }
      if (!sessionId && this.$route.path !== '/') {
        alert("YOU'VE BEEN KICKED OUT!!!!!");
        // this.$router.push({ name: "login" }); // Redirect to login page
      }
    },
    setupInactivityTimer() {
      const logout = () => {
        // Clear session data on logout
        localStorage.removeItem('session_id');
        // this.$router.push({ name: "login" }); // Redirect to login page
        if (this.$route.path !== '/') {
          // alert("YOU'VE BEEN KICKED OUT");
          localStorage.removeItem('isLoggedIn');
          this.$nextTick(() => {
            Swal.fire({
              title: "Session Timed Out",
              text: "Your session has expired. If you wish to extend or reduce the timeout, go to Settings.",
              icon: "warning",
              confirmButtonText: "OK"
            }).then(() => {
              this.$router.push({ name: "login" });
            });
          });
        }

      };

      if (localStorage.getItem('isSessionTimeoutEnabled') === 'true') {
        // Start the session timer with the timeout duration
        Session.startTimer(logout);

        // Reset the timer on any user interaction
        const events = ['mousemove', 'keydown', 'click', 'touchstart'];
        events.forEach(event =>
            window.addEventListener(event, () => Session.resetTimer(logout))
        );
      }

      // // Start the session timer
      // Session.startTimer(logout);

      // Reset the timer on any user interaction
      // const events = ['mousemove', 'keydown', 'click', 'touchstart'];
      // events.forEach(event =>
      //     window.addEventListener(event, () => Session.resetTimer(logout))
      // );
    }
  },
  beforeDestroy() {
    Session.stopTimer(); // Clean up the timer when the component is destroyed
  }
}
</script>
<style lang="scss">

// Version Update Modal Styles - GREEN theme for Petty Cash
.version-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999999;
}

.version-modal {
  background: white;
  border-radius: 16px;
  padding: 40px;
  max-width: 400px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: version-modal-pop 0.3s ease;

  &__icon {
    font-size: 64px;
    margin-bottom: 16px;
  }

  &__title {
    color: #333;
    font-size: 24px;
    margin: 0 0 16px 0;
    font-weight: 700;
  }

  &__text {
    color: #666;
    font-size: 16px;
    margin: 0 0 16px 0;
    line-height: 1.5;
  }

  &__version {
    color: #999;
    font-size: 14px;
    margin: 0 0 24px 0;
    font-family: monospace;
    background: #f5f5f5;
    padding: 8px 16px;
    border-radius: 4px;
    display: inline-block;
  }

  &__btn {
    // GREEN theme - Petty Cash brand color
    background: #10B981;
    color: white;
    border: none;
    border-radius: 8px;
    padding: 14px 32px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, transform 0.2s;

    &:hover {
      background: #059669;
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0);
    }
  }
}

@keyframes version-modal-pop {
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

</style>
